import { CloudUploadOutlined, EyeOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, message, Select, Space, Upload } from "antd";
import { COURIER_OPTION_RESOURCE, SUBMISSION_REALIZATION_INVOICE_DOCUMENT } from "apiServices/API_ENDPOINTS";
import CustomDatePicker from "common/CustomDatePicker";
import FloatingScrollbar from "common/FloatingScrollbar";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { isArrayAndHasValue } from "utils/functions";
import { getData, postData } from "../../../../apiServices/common";
import HandleNotification from "../../../../common/Notification";

const STATUS_LIST = [
  { id: 1, label: "Unpaid", value: "unpaid" },
  { id: 2, label: "Partial Paid", value: "partial_paid" },
  { id: 3, label: "Fully Paid", value: "fully_paid" },
];

const InvoiceDetails = (props) => {
  // Props
  const {
    invoiceTableData, // This is now filtered data for display
    fullInvoiceTableData, // This is the full data for submission
    paymentTerm,
    onTableDataChange,
    setInvoiceTableData,
    setFilteredInvoiceTableData,
    hasActiveFilters,
    currentFilterValues,
    applyClientSideFilter,
    view,
  } = props;

  // Antd
  const { Option } = Select;

  // Others
  const api_base_url = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  const [CourierOptionList, setCourierOptionList] = useState(null);
  const [CourierOption, setCourierOption] = useState(null);
  // selection state for copy operations
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const scrollContainerRef = useRef(null);

  const addCourierOption = async (e) => {
    e.preventDefault();
    // setCourierOption(null);
    const query = `${COURIER_OPTION_RESOURCE}`;
    const bodyData = {
      name: CourierOption,
    };

    const response = await postData(query, bodyData);

    if (response && response?.code === 200) {
      setCourierOption(null);
      HandleNotification("success", "bottomRight", "Courier Option added successfully", null);
      getCourierOptionList();
    }
  };

  const onCourierOptionChange = (event) => {
    setCourierOption(event.target.value);
  };

  // for Courier option list
  const getCourierOptionList = async (filterValues) => {
    const query = `${COURIER_OPTION_RESOURCE}`;
    const bodyData = {
      ...filterValues,
      per_page: filterValues?.per_page || 100,
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setCourierOptionList(response?.data?.data?.data);
    }
  };

  useEffect(() => {
    getCourierOptionList();
  }, []);

  const toggleRowSelection = (index, checked) => {
    const newSet = new Set(selectedRows);
    if (checked) newSet.add(index);
    else newSet.delete(index);
    setSelectedRows(newSet);
    setSelectAll(invoiceTableData && newSet.size === (invoiceTableData?.length || 0));
  };

  const toggleSelectAll = (e) => {
    const checked = e.target.checked;
    if (checked) {
      const all = new Set((invoiceTableData || []).map((_, i) => i));
      setSelectedRows(all);
      setSelectAll(true);
    } else {
      setSelectedRows(new Set());
      setSelectAll(false);
    }
  };

  const copyColumnValue = (columnKey) => {
    const rowCount = invoiceTableData?.length || 0;
    if (rowCount < 2) return;

    if (!selectedRows || selectedRows.size < 2) {
      // message.warning("Select at least 2 rows using the checkboxes to copy a column value.");
      return;
    }

    // Source = first checked row; targets = remaining checked rows only
    const selectedIndexes = Array.from(selectedRows)
      .filter((idx) => idx >= 0 && idx < rowCount)
      .sort((a, b) => a - b);

    if (selectedIndexes.length < 2) return;

    const sourceIndex = selectedIndexes[0];
    const targetIndexes = selectedIndexes.filter((idx) => idx !== sourceIndex);

    if (targetIndexes.length === 0) return;

    // Special-case file copy: copy file_id and file_url (or documents) together
    if (columnKey === "file") {
      const src = invoiceTableData?.[sourceIndex];
      const sourceFileId = src?.file_id ?? src?.documents?.id ?? null;
      const sourceFileUrl = src?.file_url ?? src?.documents?.document ?? null;
      if (!sourceFileId && !sourceFileUrl) return;

      targetIndexes.forEach((idx) => {
        if (sourceFileId) onTableDataChange(sourceFileId, idx, "file_id");
        if (sourceFileUrl) onTableDataChange(sourceFileUrl, idx, "file_url");
        onTableDataChange(true, idx, "is_copied_file");
        onTableDataChange(src?.export_details_id || null, idx, "copied_from_export_details_id");
        onTableDataChange(src?.id || null, idx, "copied_from_submission_invoice_id");
      });

      return;
    }

    const sourceValue = invoiceTableData?.[sourceIndex]?.[columnKey];
    if (typeof sourceValue === "undefined") return;

    // Apply only to resolved target rows
    targetIndexes.forEach((idx) => {
      onTableDataChange(sourceValue, idx, columnKey);

      // dependent updates
      if (columnKey === "realization_value") {
        const realizationValNum = sourceValue ? Number(sourceValue) : 0;
        const invoiceVal = invoiceTableData?.[idx]?.invoice_value ? Number(invoiceTableData[idx].invoice_value) : null;
        const shortVal = invoiceVal !== null && !Number.isNaN(invoiceVal) ? invoiceVal - realizationValNum : null;
        onTableDataChange(shortVal, idx, "short_value");

        const convRate = invoiceTableData?.[idx]?.conversion_rate ? Number(invoiceTableData[idx].conversion_rate) : 0;
        onTableDataChange(realizationValNum * convRate, idx, "converted_value");
      }

      if (columnKey === "conversion_rate") {
        const convRateNum = sourceValue ? Number(sourceValue) : 0;
        const realizationVal = invoiceTableData?.[idx]?.realization_value
          ? Number(invoiceTableData[idx].realization_value)
          : 0;
        onTableDataChange(convRateNum * realizationVal, idx, "converted_value");
      }

      if (columnKey === "invoice_value") {
        const invoiceValNum = sourceValue ? Number(sourceValue) : 0;
        const realizationVal = invoiceTableData?.[idx]?.realization_value
          ? Number(invoiceTableData[idx].realization_value)
          : 0;
        onTableDataChange(invoiceValNum - realizationVal, idx, "short_value");
      }
    });
  };

  const onChangeFileUpload = (info, index) => {
    if (info.file.status === "done" && info?.file?.response.code === 200) {
      const content = {
        id: info?.file?.response?.data?.id,
        name: info?.file?.response?.data?.document_name,
        path: info?.file?.response?.data?.document,
      };
      if (content) {
        onTableDataChange(content?.id, index, "file_id");
        onTableDataChange(content?.path, index, "file_url");
        onTableDataChange(false, index, "is_copied_file");
      }
      message.success(`${info?.file?.response?.data?.document_name} file uploaded successfully`);
    } else if (info.file.status === "done" && info?.file?.response.code !== 200) {
      message.error(`${info.file.name} file upload failed.`);
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  const calculateShortValue = (value, index) => {
    const invoice_value = invoiceTableData[index]?.invoice_value
      ? Number(invoiceTableData[index]?.invoice_value)
      : null;
    const realization_value = value ? Number(value) : null;

    // if (!invoice_value || !realization_value) return null;

    const calculatedValue = invoice_value && realization_value ? invoice_value - realization_value : null;
    onTableDataChange(calculatedValue, index, "short_value");
  };

  const removeInvoiceItem = (id) => {
    if (!id) return null;
    if (invoiceTableData && invoiceTableData?.length === 1) return null;

    // Remove from full data if fullInvoiceTableData exists
    if (fullInvoiceTableData) {
      const newFullInvoiceTableData = fullInvoiceTableData.filter((item) => item?.export_details_id !== id);
      setInvoiceTableData(newFullInvoiceTableData);

      // If filters are active, reapply them after data update
      if (hasActiveFilters && applyClientSideFilter && currentFilterValues) {
        // Use setTimeout to ensure state updates are completed first
        setTimeout(() => {
          applyClientSideFilter(currentFilterValues);
        }, 0);
      } else {
        // No filters active, just remove from displayed data
        const newInvoiceTableData = invoiceTableData.filter((item) => item?.export_details_id !== id);
        setFilteredInvoiceTableData(newInvoiceTableData);
      }
    } else {
      // Fallback for when fullInvoiceTableData doesn't exist
      const newInvoiceTableData = invoiceTableData.filter((item) => item?.export_details_id !== id);
      setInvoiceTableData(newInvoiceTableData);
      setFilteredInvoiceTableData(newInvoiceTableData);
    }
  };

  const openFileInNewTab = (file_url) => {
    const url = `${api_base_url}/${file_url}`;
    window.open(url, "_blank");
  };

  const paymentTermShowHide = (term) => {
    switch (term) {
      case "Sight":
        return false;
      case "TT":
        return false;
      default:
        return true;
    }
  };

  const updateConvertedValue = (index, realizationValue, conversionRate) => {
    const realizationValNum = realizationValue ? Number(realizationValue) : 0;
    const conversionRateNum = conversionRate ? Number(conversionRate) : 0;
    const convertedVal = realizationValNum * conversionRateNum;

    onTableDataChange(convertedVal, index, "converted_value");
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          backgroundColor: "#FFFFFF",
          padding: "10px",
          height: "700px",
          paddingBottom: "4px",
        }}
      >
        <div ref={scrollContainerRef} className="root-table table-container floating-scrollbar-target">
          <table className="pi-product-details-table">
          <thead>
            {isArrayAndHasValue(invoiceTableData) && (
              <tr>
                <th
                  className="t-header bc-java"
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    width: "600px",
                  }}
                  title="Select"
                >
                  <Checkbox checked={selectAll} onChange={toggleSelectAll} />
                </th>

                <th
                  className="t-header bc-java"
                  style={{
                    position: "sticky",
                    left: 20,
                    zIndex: 3,
                    minWidth: "16ch",
                    width: "16ch",
                    whiteSpace: "nowrap",
                  }}
                  title="Invoice No"
                >
                  Invoice No
                </th>
                <th className="t-header bc-java" title="Invoice Date">
                  Invoice Date
                </th>
                <th className="t-header bc-java" title="Invoice Value">
                  Invoice Value
                </th>
                <th className="t-header bc-java" title="Invoice Value">
                  Documents Submit Date
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("document_submission_date")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="Invoice Value">
                  Bank Reference/FDBC Number
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("fdbc_number")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="Invoice Value">
                  Bank Reference/FDBC Date
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("fdbc_date")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                {paymentTermShowHide(paymentTerm) && (
                  <>
                    <th className="t-header bc-java" title="Invoice Value">
                      Acceptance Receive Date
                      <Button
                        type="text"
                        size="small"
                        onClick={() => copyColumnValue("acceptance_rcv_date")}
                        style={{ marginLeft: 6 }}
                      >
                        📋
                      </Button>
                    </th>
                    <th className="t-header bc-java" title="Invoice Value">
                      Maturity Date
                      <Button
                        type="text"
                        size="small"
                        onClick={() => copyColumnValue("maturity_date")}
                        style={{ marginLeft: 6 }}
                      >
                        📋
                      </Button>
                    </th>
                  </>
                )}
                <th className="t-header bc-java" title="Invoice Value">
                  Realization Date
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("realization_date")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>

                <th className="t-header bc-java" title="Real Value">
                  Real Value
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("realization_value")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="Real Value">
                  Conversion Rate
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("conversion_rate")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="Real Value">
                  Converted Value
                </th>
                <th className="t-header bc-java" title="Order Quantity (PC.)">
                  Short Value
                </th>
                {/* <th className="t-header bc-java" title="Courier Option">
                  Courier Option
                </th> */}
                <th
                  className="t-header bc-java"
                  title={paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier No` : `TT No`}
                >
                  {paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier No` : `TT No`}
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("courier_no")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th
                  className="t-header bc-java"
                  title={paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier Date` : `TT Date`}
                >
                  {paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier Date` : `TT Date`}
                  <Button
                    type="text"
                    size="small"
                    onClick={() => copyColumnValue("courier_date")}
                    style={{ marginLeft: 6 }}
                  >
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="Status">
                  Status
                  <Button type="text" size="small" onClick={() => copyColumnValue("status")} style={{ marginLeft: 6 }}>
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="File Upload">
                  File
                  <Button type="text" size="small" onClick={() => copyColumnValue("file")} style={{ marginLeft: 6 }}>
                    📋
                  </Button>
                </th>
                <th className="t-header bc-java" title="">
                  Action
                </th>
              </tr>
            )}
          </thead>

          <tbody>
            {isArrayAndHasValue(invoiceTableData) ? (
              <>
                {invoiceTableData?.map((invoiceItem, index) => {
                  return (
                    <tr key={index}>
                      <td
                        className="t-body"
                        title="Select"
                        style={{
                          position: "sticky",
                          background: "#ffffff",
                          left: 0,
                          zIndex: 2,
                        }}
                      >
                        <Checkbox
                          checked={selectedRows.has(index)}
                          onChange={(e) => toggleRowSelection(index, e.target.checked)}
                        />
                      </td>

                      <td
                        className="t-body"
                        title="Invoice No"
                        style={{
                          position: "sticky",
                          background: "#ffffff",
                          left: 20,
                          zIndex: 2,
                          minWidth: "20ch",
                          width: "20ch",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span style={{ whiteSpace: "nowrap" }}>{invoiceItem.invoice_no}</span>
                      </td>
                      <td className="t-body sticky" title="Invoice Date">
                        <CustomDatePicker
                          // format="YYYY-MM-DD"
                          style={{
                            width: "110px",
                          }}
                          value={moment(invoiceItem.invoice_date)}
                          disabled
                          size="small"
                        />
                      </td>
                      <td className="t-body sticky" title="Invoice Value">
                        <span>{invoiceItem.invoice_value}</span>
                      </td>
                      <td className="t-body sticky" title="Documents Submit Date">
                        <CustomDatePicker
                          // format={"YYYY-MM-DD"}
                          style={{
                            width: "110px",
                          }}
                          size="small"
                          value={
                            invoiceItem.document_submission_date ? moment(invoiceItem.document_submission_date) : null
                          }
                          onChange={(date, dateString) => {
                            onTableDataChange(dateString, index, "document_submission_date");
                          }}
                          disabled={view}
                        />
                      </td>
                      <td className="t-body sticky" title="Bank Reference/FDBC Number">
                        <Input
                          onChange={(event) => {
                            onTableDataChange(event.target.value, index, "fdbc_number");
                          }}
                          value={invoiceItem?.fdbc_number || ""}
                          placeholder="Bank Reference/FDBC Number"
                          size="small"
                          disabled={view}
                        />
                      </td>
                      <td className="t-body sticky" title="Bank Reference/FDBC Date">
                        <CustomDatePicker
                          // format="YYYY-MM-DD"
                          style={{
                            width: "110px",
                          }}
                          value={invoiceItem.fdbc_date ? moment(invoiceItem.fdbc_date) : null}
                          onChange={(date, dateString) => {
                            onTableDataChange(dateString, index, "fdbc_date");
                          }}
                          size="small"
                          disabled={view}
                        />
                      </td>
                      {paymentTermShowHide(paymentTerm) && (
                        <>
                          <td className="t-body sticky" title="Acceptance Receive Date">
                            <CustomDatePicker
                              // format="YYYY-MM-DD"
                              style={{
                                width: "110px",
                              }}
                              value={invoiceItem.acceptance_rcv_date ? moment(invoiceItem.acceptance_rcv_date) : null}
                              onChange={(date, dateString) => {
                                onTableDataChange(dateString, index, "acceptance_rcv_date");
                              }}
                              size="small"
                              disabled={view}
                            />
                          </td>
                          <td className="t-body sticky" title="Maturity Date">
                            <CustomDatePicker
                              // format="YYYY-MM-DD"
                              style={{
                                width: "110px",
                              }}
                              value={invoiceItem.maturity_date ? moment(invoiceItem.maturity_date) : null}
                              onChange={(date, dateString) => {
                                onTableDataChange(dateString, index, "maturity_date");
                              }}
                              size="small"
                              disabled={view}
                            />
                          </td>
                        </>
                      )}
                      <td className="t-body sticky" title="Realization Date">
                        <CustomDatePicker
                          // format="YYYY-MM-DD"
                          style={{
                            width: "110px",
                          }}
                          value={invoiceItem.realization_date ? moment(invoiceItem.realization_date) : null}
                          onChange={(date, dateString) => {
                            onTableDataChange(dateString, index, "realization_date");
                          }}
                          size="small"
                          disabled={view}
                        />
                      </td>

                      <td className="t-body sticky" title="Real Value">
                        <Input
                          onChange={(event) => {
                            onTableDataChange(event.target.value, index, "realization_value");
                            calculateShortValue(event.target.value, index);

                            const conversionRateVal = invoiceTableData[index]?.conversion_rate;
                            updateConvertedValue(index, event.target.value, conversionRateVal);
                          }}
                          value={invoiceItem?.realization_value || ""}
                          size="small"
                          placeholder="Real Value"
                          style={{ width: "100px" }}
                          disabled={view}
                        />
                      </td>
                      <td className="t-body sticky" title="Conversion Rate">
                        <Input
                          onChange={(event) => {
                            let val = event.target.value;

                            // Regex to allow only numbers with up to 2 decimal places
                            if (val === "" || /^\d+(\.\d{0,2})?$/.test(val)) {
                              onTableDataChange(val, index, "conversion_rate");

                              const realizationVal = invoiceTableData[index]?.realization_value;
                              updateConvertedValue(index, realizationVal, val);
                            }
                          }}
                          value={invoiceItem?.conversion_rate || ""}
                          size="small"
                          placeholder="Conversion Rate"
                          style={{ width: "100px" }}
                          disabled={view}
                        />
                      </td>
                      <td className="t-body sticky" title="Converted Value">
                        <Input
                          value={invoiceItem?.converted_value}
                          disabled
                          size="small"
                          placeholder="Converted Value"
                          style={{ width: "100px" }}
                        />
                      </td>
                      <td className="t-body sticky" title="Short Value">
                        <Input
                          value={invoiceItem?.short_value}
                          disabled
                          size="small"
                          placeholder="Short Value"
                          style={{ width: "100px" }}
                        />
                      </td>
                      {/* <td className="t-body sticky" title="Courier Option">
                        <Select
                          style={{
                            width: "100%",
                          }}
                          placeholder="Please select Courier Option"
                          onChange={(value) => {
                            onTableDataChange(
                              value,
                              index,
                              "courier_option_id",
                            );
                          }}
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return option?.label
                              ?.toLowerCase()
                              ?.includes(input?.toLowerCase());
                          }}
                          defaultValue={invoiceItem?.courier_option_id || null}
                          dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider
                                style={{
                                  margin: "8px 0",
                                }}
                              />
                              <Space
                                style={{
                                  padding: "0 8px 4px",
                                }}
                              >
                                <Input
                                  placeholder="Please enter Courier Option"
                                  value={CourierOption}
                                  onChange={onCourierOptionChange}
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                                <Button
                                  type="text"
                                  icon={<PlusOutlined />}
                                  onClick={addCourierOption}
                                >
                                  Add item
                                </Button>
                              </Space>
                            </>
                          )}
                          options={CourierOptionList?.map((item) => ({
                            label: item?.name,
                            value: item?.id,
                          }))}
                        />
                      </td> */}
                      <td
                        className="t-body sticky"
                        title={paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier No` : `TT No`}
                      >
                        <Input
                          onChange={(event) => onTableDataChange(event.target.value, index, "courier_no")}
                          value={invoiceItem?.courier_no || ""}
                          size="small"
                          placeholder={paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier No` : `TT No`}
                          style={{ width: "100px" }}
                          disabled={view}
                        />
                      </td>
                      <td
                        className="t-body sticky"
                        title={paymentTerm === "Deferred" || paymentTerm === "Sight" ? `Courier Date` : `TT Date`}
                      >
                        <CustomDatePicker
                          // format={"YYYY-MM-DD"}
                          style={{
                            width: "110px",
                          }}
                          size="small"
                          value={invoiceItem.courier_date ? moment(invoiceItem.courier_date) : null}
                          onChange={(date, dateString) => {
                            onTableDataChange(dateString, index, "courier_date");
                          }}
                          disabled={view}
                        />
                      </td>
                      <td className="t-body sticky" title="Status">
                        <Select
                          showSearch
                          placeholder="Please select Status"
                          onChange={(value) => {
                            onTableDataChange(value, index, "status");
                          }}
                          optionFilterProp="children"
                          filterOption={(input, option) => {
                            return option?.label?.toLowerCase()?.includes(input?.toLowerCase());
                          }}
                          value={invoiceItem?.status || null}
                          size="small"
                          style={{ width: "150px" }}
                          disabled={view}
                        >
                          {STATUS_LIST?.length > 0 &&
                            STATUS_LIST?.map((statusItem) => {
                              return (
                                <Option value={statusItem?.value} key={statusItem?.id} label={statusItem?.label}>
                                  {statusItem?.label}
                                </Option>
                              );
                            })}
                        </Select>
                      </td>
                      <td className="t-body sticky" title="">
                        <Space>
                          <Upload
                            action={api_base_url + SUBMISSION_REALIZATION_INVOICE_DOCUMENT}
                            accept="image/*,.pdf, .doc, .xlsx"
                            headers={{
                              authorization: `Bearer ${token}`,
                            }}
                            showUploadList={false}
                            onChange={(info) => onChangeFileUpload(info, index)}
                          >
                            <Button type="primary" icon={<CloudUploadOutlined />} disabled={view} />
                          </Upload>
                          {(invoiceItem?.file_url || invoiceItem?.documents) && (
                            <Button
                              icon={<EyeOutlined />}
                              onClick={() =>
                                invoiceItem?.file_url
                                  ? openFileInNewTab(invoiceItem?.file_url)
                                  : openFileInNewTab(invoiceItem?.documents?.document)
                              }
                              disabled={view}
                            />
                          )}
                        </Space>
                      </td>
                      <td className="t-body sticky" title="">
                        <Button
                          size="small"
                          danger
                          onClick={() => removeInvoiceItem(invoiceItem?.export_details_id)}
                          disabled={view}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </>
            ) : (
              ""
            )}
          </tbody>
          </table>
        </div>
      </div>
      <FloatingScrollbar
        targetRef={scrollContainerRef}
        contentKey={`${paymentTerm}-${invoiceTableData?.length ?? 0}`}
      />
    </>
  );
};

export default InvoiceDetails;
